// collector/dayModel.js
//
// The optimistic side of the collector's action layer (collector-app P1-C
// part C) — PURE functions over the day document (backend brief
// 2026-09-30-collector-day-read). No store, no network, no Vue.
//
// THE MODEL: what the screens show is
//
//     rebaseDay(serverDay, overlay)
//
// = the last document the server sent, with every tap it has not caught up
// with yet applied on top, in the order they were made (`seq`). The overlay
// holds the taps still queued or on the wire, the taps the server applied
// that the next read has not reflected yet, and the lines a conflict or a
// refusal handed back (`current`, the row after the attempt). A refusal
// "reverts" simply by leaving the overlay; a fresh read replaces the base
// and prunes what it already contains. Phase 2 (offline) reuses this as is:
// the morning's document is the base, the persisted queue is the overlay.
//
// What a tap changes locally mirrors what the backend will derive — the line's
// outcome, the counters, the cash advisory — but never a «خلّصت»: a shop is
// never made done here, only moved out of done when a line reopens.

/** Outcomes the backend counts nowhere (the leader's skip / cancel). */
const RELEASED = new Set(['cancelled', 'skipped']);

const DONE_OR_CLOSED = new Set(['done_matched', 'done_diff', 'done_no_paper', 'closed_by_collector']);

/** A deep copy of a plain document (never a reactive proxy). */
export function cloneDay(day) {
  if (day == null) return null;
  return typeof structuredClone === 'function' ? structuredClone(day) : JSON.parse(JSON.stringify(day));
}

/** The shop and line of a plan item in the day, or null. */
export function findLine(day, planItemId) {
  const id = String(planItemId);
  for (const shop of day?.shops ?? []) {
    const line = (shop.lines ?? []).find((candidate) => String(candidate.id) === id);
    if (line) return { shop, line };
  }
  return null;
}

/** 0/1 tallies of a line as the backend counts it (released lines count nowhere). */
function tally(line) {
  if (RELEASED.has(line.outcome) || line.movedAway) return { total: 0, collected: 0, oos: 0, pending: 0 };
  const collected = line.outcome === 'collected' ? 1 : 0;
  const oos = line.outcome === 'out_of_stock' ? 1 : 0;
  return { total: 1, collected, oos, pending: 1 - collected - oos };
}

/**
 * What a line puts on the cash advisory: the backend's rule, found qty ??
 * ordered qty × my shelf price ?? list price, and the member's deposit, on a
 * COLLECTED line only. This is the ONE place that rule is mirrored on the
 * phone (CollectorDayBuilder::grossSpentCents / netSpentCents) — the money
 * views (moneyView.js) read it from here and never write it a second time.
 * `qty` and `price` are the two factors of `gross`, for a screen that prints
 * the sum.
 *
 * @returns {{ gross: number, deposit: number, qty: number, price: number }}
 */
export function lineSpend(line) {
  if (line?.outcome !== 'collected' || line.movedAway) return { gross: 0, deposit: 0, qty: 0, price: 0 };
  const qty = Number(line.collectorFacts?.foundQty ?? line.qty ?? 0);
  const price = Number(line.collectorFacts?.priceCents ?? line.unitPriceCents ?? 0);
  return { gross: qty * price, deposit: Number(line.depositCents) || 0, qty, price };
}

/**
 * A found quantity as the server keeps it: between 0 and what was ordered
 * (CollectionPlanService::recordCollectorFacts clamps to the row's ordered
 * quantity). The report sheet does not stop him typing more, and an unclamped
 * figure would run through `lineSpend` into every amount on the screen.
 */
function clampFound(foundQty, line) {
  const ordered = Number(line?.qty);
  const found = Math.max(0, Number(foundQty) || 0);
  return Number.isFinite(ordered) && ordered > 0 ? Math.min(found, ordered) : found;
}

function add(target, key, delta) {
  if (!target || delta === 0) return;
  target[key] = (Number(target[key]) || 0) + delta;
}

/** Change one line, then carry the change into the counters, progress, cash and shop state. */
function mutateLine(day, planItemId, mutate) {
  const hit = findLine(day, planItemId);
  if (!hit) return null;
  const { shop, line } = hit;
  const tallyBefore = tally(line);
  const spendBefore = lineSpend(line);
  const stateBefore = shop.state;

  mutate(line, shop);

  const tallyAfter = tally(line);
  const spendAfter = lineSpend(line);
  shop.counters = shop.counters ?? {};
  day.progress = day.progress ?? {};
  for (const key of ['total', 'collected', 'oos', 'pending']) {
    const delta = tallyAfter[key] - tallyBefore[key];
    add(shop.counters, key, delta);
    add(day.progress, key === 'total' ? 'lines' : key, delta);
  }

  const grossDelta = spendAfter.gross - spendBefore.gross;
  const depositDelta = spendAfter.deposit - spendBefore.deposit;
  // A wallet shop's goods cost him no cash: the club pays the shop's wallet.
  if (day.cash && !shop.paidByWallet) {
    add(day.cash, 'spentCents', grossDelta);
    add(day.cash, 'depositsCents', depositDelta);
    add(day.cash, 'withMeCents', -(grossDelta - depositDelta));
  }
  add(shop, 'spentCents', grossDelta - depositDelta);

  settleShopState(day, shop, stateBefore, tallyAfter.pending > tallyBefore.pending);
  return hit;
}

/**
 * The only shop-state moves made locally: a first mark takes a shop out of
 * «not started», and a line turned back to pending (a reopen, or the leader's
 * answer) takes a done or closed shop out of done — that line carries no
 * closed-shop note, so the backend will not read it done or closed either.
 * Never INTO done: «خلّصت» is the backend's word.
 */
function settleShopState(day, shop, stateBefore, lineBecamePending) {
  const lines = (shop.lines ?? []).filter((line) => !RELEASED.has(line.outcome) && !line.movedAway);
  const anyMarked = lines.some((line) => line.outcome === 'collected' || line.outcome === 'out_of_stock');

  if (shop.state === 'not_started' && anyMarked) shop.state = 'in_progress';

  if (lineBecamePending && DONE_OR_CLOSED.has(stateBefore) && shop.state === stateBefore) {
    shop.state = anyMarked ? 'in_progress' : 'not_started';
    add(day.progress, 'shopsDone', -1);
  }
}

function planRunning(day) {
  return day?.plan?.status === 'in_progress';
}

function markLine(day, action, outcome) {
  mutateLine(day, action.planItemId, (line) => {
    if (line.outcome !== 'pending' && line.outcome !== 'in_progress') return;
    line.outcome = outcome;
    line.canMark = false;
    line.canReopen = planRunning(day) && !line.leaderState?.verified;
  });
}

function reopenLine(day, action) {
  mutateLine(day, action.planItemId, (line) => {
    if (line.outcome !== 'collected' && line.outcome !== 'out_of_stock') return;
    line.outcome = 'pending';
    line.canReopen = false;
    line.canMark = planRunning(day) && !line.activeQuestion;
  });
}

function recordFacts(day, action) {
  const payload = action.payload ?? {};
  mutateLine(day, action.planItemId, (line) => {
    const facts = { ...(line.collectorFacts ?? {}) };
    if (payload.foundQty != null) facts.foundQty = clampFound(payload.foundQty, line);
    if (payload.priceCents != null) facts.priceCents = payload.priceCents;
    if (payload.note != null) facts.note = payload.note;
    // The latest list replaces the stored one; `[]` clears it, a missing key leaves it.
    if (Array.isArray(payload.options)) facts.options = [...payload.options];
    facts.reportedAt = action.clientAt;
    line.collectorFacts = facts;
  });
}

/**
 * «اشتريته» / «مش لقيته» on her answer (collector-app P3-C, L-F): a REPORT for
 * the leader, never a mark — the outcome, the counters and the cash stay as
 * they are (the leader's close at the station applies her option and bills).
 * Only the facts move: what I reported and when, and the quantity on «اشتريته».
 * `answerReport` is read back by lineView `answerReportKind`; the server has no
 * such field yet, so after a read the note's opening words say the same.
 * Ignored when no answered question is on the line — the server's answer rules.
 */
function reportOnAnswer(day, action, kind) {
  mutateLine(day, action.planItemId, (line) => {
    if (!line.activeQuestion?.answered) return;
    const facts = { ...(line.collectorFacts ?? {}) };
    facts.answerReport = kind;
    facts.reportedAt = action.clientAt;
    if (kind === 'bought' && action.payload?.foundQty != null) facts.foundQty = clampFound(action.payload.foundQty, line);
    line.collectorFacts = facts;
  });
}

/**
 * «المحل مقفول» / «ماوصلناش»: the backend writes its note on every pending
 * line of mine there, and the shop then reads `closed_by_collector` (L-C).
 * Locally only the shop state moves; the note's words stay the backend's.
 */
function leaveShop(day, action) {
  const supplierId = action.payload?.supplierId;
  const shop = (day?.shops ?? []).find((candidate) => String(candidate.supplierId) === String(supplierId));
  if (!shop || shop.state === 'closed_by_collector') return;
  const unmarked = (shop.lines ?? []).some(
    (line) => (line.outcome === 'pending' || line.outcome === 'in_progress') && !line.activeQuestion && !line.movedAway,
  );
  if (!unmarked) return;
  if (!DONE_OR_CLOSED.has(shop.state)) add(day.progress, 'shopsDone', 1);
  shop.state = 'closed_by_collector';
}

/**
 * What tells one answer of hers from the next on a line: the moment of her
 * CURRENT answer (`respondedAt` moves when she changes it). Null while the line
 * has no answered question.
 */
function answerStamp(line) {
  const question = line?.activeQuestion;
  return question?.answered === true ? String(question.respondedAt ?? 'answered') : null;
}

/**
 * The lines of `next` whose live question is answered now and was NOT — or not
 * with this same answer — in `prev`: what a read has brought that the
 * collector has not been told about (collector-app P3-C, L-G; one notice per
 * (line, `respondedAt`)). Both are SERVER documents, never the overlay — a tap
 * of his own changes nothing here. With no `prev` (the first read on a phone
 * that kept no day) every answered question is new to him.
 *
 * @param {object|null} prev - the document the screens showed before this read
 * @param {object|null} next - the document the read just brought
 * @returns {Array<{ shop: object, line: object }>}
 */
export function newlyAnswered(prev, next) {
  if (!next) return [];
  const before = new Map();
  for (const shop of prev?.shops ?? []) {
    for (const line of shop.lines ?? []) before.set(String(line.id), answerStamp(line));
  }
  const fresh = [];
  for (const shop of next.shops ?? []) {
    for (const line of shop.lines ?? []) {
      if (RELEASED.has(line.outcome) || line.movedAway) continue;
      const stamp = answerStamp(line);
      if (stamp == null || before.get(String(line.id)) === stamp) continue;
      fresh.push({ shop, line });
    }
  }
  return fresh;
}

/**
 * Apply one tap's optimistic effect to `day` (mutates it). Unknown types and
 * lines not in the day are ignored — the server's answer is the truth.
 */
export function applyAction(day, action) {
  if (!day || !action) return day;
  switch (action.type) {
    case 'collected':
      markLine(day, action, 'collected');
      break;
    case 'out_of_stock':
      markLine(day, action, 'out_of_stock');
      break;
    case 'reopen':
      reopenLine(day, action);
      break;
    case 'facts':
      recordFacts(day, action);
      break;
    case 'shop_left':
      leaveShop(day, action);
      break;
    case 'bought':
      reportOnAnswer(day, action, 'bought');
      break;
    case 'not_found':
      reportOnAnswer(day, action, 'not_found');
      break;
    default:
      break;
  }
  return day;
}

/**
 * Replace a line's state with the server's `current` (the row after an
 * attempt: `{ outcome, verified, rejectedReason, cancelled, assignedToMe,
 * planStatus, questionOpen }`). The flags the screens read — `canMark`,
 * `canReopen` — follow from it the way the day read derives them.
 */
export function applyCurrent(day, planItemId, current) {
  if (!day || !current) return day;
  if (current.planStatus && day.plan) day.plan.status = current.planStatus;
  mutateLine(day, planItemId, (line) => {
    if (current.outcome) line.outcome = current.outcome;
    line.leaderState = {
      ...(line.leaderState ?? {}),
      verified: current.verified === true,
      rejectedReason: current.rejectedReason ?? null,
      cancelled: current.cancelled === true,
    };
    if (current.assignedToMe === false) line.movedAway = true;
    if (current.questionOpen === true && !line.activeQuestion) {
      line.activeQuestion = { question: null, sentAt: null, answered: false };
    }
    if (current.questionOpen === false && line.activeQuestion) line.activeQuestion = null;

    const running = (current.planStatus ?? day.plan?.status) === 'in_progress';
    const mine = current.assignedToMe !== false;
    const marked = line.outcome === 'collected' || line.outcome === 'out_of_stock';
    line.canMark = running && mine && line.outcome === 'pending' && !line.activeQuestion;
    line.canReopen = running && mine && marked && current.verified !== true;
  });
  return day;
}

/**
 * The collector's own photos the server does not have yet, drawn from their
 * Blobs on the lines they belong to (collector-app P2-C): he sees the picture
 * the moment he takes it, with or without signal. A local photo whose server
 * twin is already in the document (the report was applied and the day re-read,
 * the file not uploaded yet) REPLACES that tile, so it is never shown twice and
 * the picture that does not exist on the server yet is never a broken image.
 * The server names a shelf photo `…/app-<uuid>.jpg`, which is how the twin is
 * found. A photo with no twin yet is also counted in `collectorFacts.photoUrls`,
 * so the five-photo room and the card's «مع صور» agree with what he sees.
 * Mutates `day` (a clone from `rebaseDay`).
 *
 * @param {object|null} day
 * @param {Array<{uuid: string, lineId: string, preview: string}>} locals
 */
export function withLocalPhotos(day, locals = []) {
  if (!day) return day;
  for (const local of locals) {
    const hit = findLine(day, local.lineId);
    if (!hit) continue;
    const { line } = hit;
    const photos = Array.isArray(line.photos) ? [...line.photos] : [];
    const tile = {
      url: local.preview,
      thumbUrl: local.preview,
      mediumUrl: null,
      thumbIsOriginal: true,
      label: '',
      tier: 'collector',
      local: true,
      uuid: local.uuid,
    };
    const tail = `/app-${String(local.uuid).toLowerCase()}.jpg`;
    const twin = photos.findIndex((photo) => photo?.tier === 'collector' && String(photo.url ?? '').toLowerCase().includes(tail));
    if (twin >= 0) {
      photos[twin] = tile;
    } else {
      photos.push(tile);
      const facts = { ...(line.collectorFacts ?? {}) };
      facts.photoUrls = [...(Array.isArray(facts.photoUrls) ? facts.photoUrls : []), local.preview];
      line.collectorFacts = facts;
    }
    line.photos = photos;
  }
  return day;
}

/**
 * A shop whose invoice is filed ON THE PHONE and not sent yet (collector-offline
 * P1-B; collector/invoiceQueue.js `queued`) carries `invoiceQueued` — what he
 * filed and when. Only filings of THIS day's plan, on the shop of that
 * supplier. It is a flag for the screens (dayView.js: the chip, «دلوقتي عندك»)
 * and nothing else: the shop's `state`, the progress and the cash stay exactly
 * as they were — «خلّصت» is the backend's word, and it arrives with a read
 * after the filing is sent. Mutates `day` (a clone from `rebaseDay`).
 *
 * @param {object|null} day
 * @param {Array<{planId: string, supplierId: string, amountCents: number|null, noPaper: boolean, filedAt: string}>} queued
 */
export function withQueuedInvoices(day, queued = []) {
  if (!day) return day;
  for (const filing of queued) {
    if (!ofPlan(filing, day)) continue;
    const shop = (day.shops ?? []).find(
      (candidate) => candidate?.supplierId != null && String(candidate.supplierId) === String(filing.supplierId),
    );
    if (!shop) continue;
    shop.invoiceQueued = {
      amountCents: filing.amountCents ?? null,
      noPaper: Boolean(filing.noPaper),
      filedAt: filing.filedAt ?? null,
    };
  }
  return day;
}

/** A filing of the plan this day document is. */
function ofPlan(filing, day) {
  return filing?.planId != null && day?.id != null && String(filing.planId) === String(day.id);
}

/**
 * The day the screens render: the server's document with the overlay applied
 * in tap order, then the collector's own pictures not on the server yet, then
 * the invoices filed on the phone and not sent. Each overlay entry is plain
 * data — `{ seq, planItemId, action?, current? }` — `action` re-applies a tap,
 * `current` pins a line to the server's answer.
 *
 * With nothing to apply the server's own document is handed back; anything
 * written on top is written on a CLONE, never on `serverDay`.
 *
 * @param {object|null} serverDay
 * @param {Array<{seq: number, planItemId: string, action?: object, current?: object|null}>} overlay
 * @param {Array<{uuid: string, lineId: string, preview: string}>} [locals] see `withLocalPhotos`
 * @param {Array<object>} [queued] see `withQueuedInvoices`
 */
export function rebaseDay(serverDay, overlay = [], locals = [], queued = []) {
  if (!serverDay) return null;
  const filings = queued.filter((filing) => ofPlan(filing, serverDay));
  if (!overlay.length && !locals.length && !filings.length) return serverDay;
  const day = cloneDay(serverDay);
  const ordered = [...overlay].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  for (const entry of ordered) {
    if (entry.action) applyAction(day, entry.action);
    if (entry.current) applyCurrent(day, entry.planItemId, entry.current);
  }
  return withQueuedInvoices(withLocalPhotos(day, locals), filings);
}
